/* Copyright (c) 2024 MOCA Systems, Inc.	All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { DateAdapter, MatDateFormats } from '@angular/material/core';
import { DangerousDateCache } from '@touchplan/common';
import dayjs from 'dayjs';
import { default as objectSupport } from 'dayjs/plugin/objectSupport';

dayjs.extend(objectSupport);

type TYear = `${number}${number}${number}${number}`;
type TMonth = `${number}${number}`;
type TDay = `${number}${number}`;
export type TDateISODate = `${TYear}-${TMonth}-${TDay}`;

const fullFormat = 'YYYY-MM-DD';

/**
 * Creates an array and fills it with values.
 * Ripped off from the native-date-adapter
 */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
	const valuesArray = Array(length);
	for (let i = 0; i < length; i++) {
		valuesArray[i] = valueFunction(i);
	}
	return valuesArray;
}

//something to consider in the future, create a dayjs adapter
//then extend from that to implement the iso8601 adapter
//also to consider, _full internationalization_ support
@Injectable()
export class Iso8601DateAdapter extends DateAdapter<TDateISODate> implements OnDestroy {
	private dateCache = new DangerousDateCache();

	public getYear(date: TDateISODate): number {
		return this.dateCache.getCachedDayjs(date).year();
	}
	public getMonth(date: TDateISODate): number {
		return this.dateCache.getCachedDayjs(date).month();
	}
	public getDate(date: TDateISODate): number {
		return this.dateCache.getCachedDayjs(date).date();
	}
	public getDayOfWeek(date: TDateISODate): number {
		return this.dateCache.getCachedDayjs(date).day();
	}
	public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
		return range(12, (i) => {
			switch (style) {
				case 'long':
					return dayjs().month(i).format('MMMM');
				//...narrow, short and default can all be the short style until we internationalize
				case 'short':
				case 'narrow':
				default:
					return dayjs().month(i).format('MMM');
			}
		});
	}

	public getDateNames(): string[] {
		return range(31, (i) => '' + (i + 1));
	}

	public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
		return range(7, (i) => {
			switch (style) {
				case 'long':
					return dayjs().day(i).format('dddd');
				case 'short':
					return dayjs().day(i).format('ddd');
				case 'narrow':
				default:
					return dayjs().day(i).format('dd');
			}
		});
	}
	public getYearName(date: TDateISODate): string {
		return this.dateCache.getCachedDayjs(date).format('YYYY');
	}
	public getFirstDayOfWeek(): number {
		// return 1; //Monday
		return 0;
	}
	public getNumDaysInMonth(date: TDateISODate): number {
		return this.dateCache.getCachedDayjs(date).daysInMonth();
	}
	public clone(date: TDateISODate): TDateISODate {
		//could probably just return the date, string and all that
		return this.dateCache.getFormat(date, fullFormat) as TDateISODate;
	}
	public createDate(year: number, month: number, date: number): TDateISODate {
		return dayjs({
			year,
			month,
			day: date,
		}).format(fullFormat) as TDateISODate;
	}
	public today(): TDateISODate {
		return this.dateCache.todayString as TDateISODate;
	}
	public parse(value: any, parseFormat: any): TDateISODate | null {
		if (typeof value === 'number') {
			return dayjs(value).format(fullFormat) as TDateISODate;
		}
		return this.dateCache.getFormat(value, fullFormat) as TDateISODate;
	}
	public format(date: TDateISODate, displayFormat: any): string {
		if (displayFormat && typeof displayFormat !== 'string') {
			throw new Error(
				'displayFormat does not match, { provide: MAT_DATE_FORMATS, useValue: ISO8601_DATE_FORMATS} expected'
			);
		}
		return this.dateCache.getFormat(date, displayFormat || undefined);
	}
	public addCalendarYears(date: TDateISODate, years: number): TDateISODate {
		return this.dateCache.getCachedDayjs(date).add(years, 'year').format(fullFormat) as TDateISODate;
	}
	public addCalendarMonths(date: TDateISODate, months: number): TDateISODate {
		return this.dateCache.getCachedDayjs(date).add(months, 'month').format(fullFormat) as TDateISODate;
	}
	public addCalendarDays(date: TDateISODate, days: number): TDateISODate {
		return this.dateCache.addDays(date, days) as TDateISODate;
	}
	public toIso8601(date: TDateISODate): string {
		return date;
	}
	public isDateInstance(obj: any): boolean {
		return typeof obj === 'string' && obj.match(/^\d{4}-\d{2}-\d{2}$/) !== null;
	}
	public isValid(date: TDateISODate): boolean {
		//sanity check what dayjs considers valid
		return this.isDateInstance(date) && this.dateCache.getCachedDayjs(date).isValid();
	}
	public invalid(): TDateISODate {
		return null; //??
	}

	// will be called automatically if this is provided in a component
	public ngOnDestroy(): void {
		this.dateCache.clearCache();
	}
}

export const ISO8601_DATE_FORMATS: MatDateFormats = {
	parse: {
		dateInput: ['YYYY-MM-DD'],
	},
	display: {
		dateInput: 'YYYY-MM-DD',
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
	},
};
